import { router } from "@inertiajs/react";
import { useQueryClient } from "@tanstack/react-query";
import { Body } from "@/sprout/typography";
import { PrimaryButton, PrimaryButtonLink, TertiaryButton } from "@sprout/buttons";
import type { GhrTask } from "@/types/task";
import useTranslation from "@hooks/use_translation";
import useTaskStatusText from "@hooks/use_task_status_text";
import useVerificationFlow from "@hooks/use_verification_flow";
import GHR_TASK_CONFIG, { type GhrTaskConfig } from "@/config/ghr_task_config";
import { identityVerificationPartnerConfig } from "@/utils/identity_verification_provider";
import { isTaskOverdue } from "@/utils/task_utils";

const IdentityVerificationAction = ({ task }: { task: GhrTask }) => {
  const { t } = useTranslation("identity_verification");
  const { openModal, modal } = useVerificationFlow(task);

  return (
    <>
      {task.status === "processing_data" ? (
        <PrimaryButton size="medium" disabled>
          {t("status.processing_data.label")}
        </PrimaryButton>
      ) : task.status === "awaiting_user_input" && task.verification_url ? (
        <PrimaryButtonLink size="medium" href={task.verification_url} external>
          {t("cta.continue_verification")}
        </PrimaryButtonLink>
      ) : (
        <PrimaryButton size="medium" onClick={openModal}>
          {t("cta.verify_identity")}
        </PrimaryButton>
      )}
      {modal}
    </>
  );
};

const TaskBannerActions = ({
  task,
  config,
  isThirdPartyTakeHomeTest,
  isIdentityVerification,
  handleDismiss,
  invalidateQuery,
}: {
  task: GhrTask;
  config: GhrTaskConfig;
  isThirdPartyTakeHomeTest: boolean;
  isIdentityVerification: boolean;
  handleDismiss: () => void;
  invalidateQuery: () => void;
}) => {
  const { t } = useTranslation("tasks");

  if (isThirdPartyTakeHomeTest) {
    return <TertiaryButton onClick={handleDismiss}>{t("common.ok")}</TertiaryButton>;
  }

  return (
    <>
      <TertiaryButton onClick={handleDismiss}>{t("common.dismiss")}</TertiaryButton>
      {isIdentityVerification ? (
        <IdentityVerificationAction task={task} />
      ) : (
        task.url && (
          <PrimaryButtonLink href={task.url} size="medium" external onClick={invalidateQuery}>
            {t(config.actionTextKey)}
          </PrimaryButtonLink>
        )
      )}
    </>
  );
};

const ApplicationTaskBanner = ({ task }: { task: GhrTask }) => {
  const { t } = useTranslation("tasks");
  const queryClient = useQueryClient();
  const statusText = useTaskStatusText(task);
  const isIdentityVerification = task.taskable_type === "IdentityVerificationTask";
  const isThirdPartyTakeHomeTest = task.taskable_type === "TakeHomeTest" && !task.url;
  const config = isThirdPartyTakeHomeTest
    ? GHR_TASK_CONFIG.ThirdPartyTakeHomeTest
    : GHR_TASK_CONFIG[task.taskable_type];

  if (!config) {
    return null;
  }

  const canRenderWithoutUrl = isIdentityVerification || isThirdPartyTakeHomeTest;

  if (!task.url && !canRenderWithoutUrl) {
    return null;
  }

  const Icon = config.icon;
  const title = isIdentityVerification
    ? t(config.titleKey, {
        provider_name: identityVerificationPartnerConfig(task.partner).name,
      })
    : t(config.titleKey);

  const invalidateQuery = () => queryClient.invalidateQueries({ queryKey: ["tasks"] });
  const handleDismiss = () =>
    router.post(
      `/tasks/${task.id}/dismiss`,
      {},
      { preserveScroll: true, preserveState: true, onSuccess: invalidateQuery },
    );

  const isOverdue = isTaskOverdue(task);
  const backgroundClass = isOverdue ? "bg-[var(--extra-light-red)]" : "bg-pale-blue";
  const iconBackgroundClass = isOverdue ? "bg-poppy-e50" : "bg-iris-e50";

  return (
    <div className={`application-task-banner w-full ${backgroundClass} rounded-lg px-6 py-4`}>
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1">
            <div
              className={`flex items-center justify-center ${iconBackgroundClass} rounded w-[30px] h-[30px]`}
            >
              <Icon size="sm" />
            </div>
            <Body className="!text-xs !ml-1" metadata>
              {statusText}
            </Body>
          </div>
          <Body medium>{title}</Body>
        </div>
        <div className="flex items-center gap-4 md:shrink-0">
          <TaskBannerActions
            task={task}
            config={config}
            isThirdPartyTakeHomeTest={isThirdPartyTakeHomeTest}
            isIdentityVerification={isIdentityVerification}
            handleDismiss={handleDismiss}
            invalidateQuery={invalidateQuery}
          />
        </div>
      </div>
    </div>
  );
};

export default ApplicationTaskBanner;
